import { observable, action } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';

import { App, AppManager, Input } from 'core/index';
import { analytics } from 'core/analytics';
import { WizardStore } from 'libs/wizards/app';

import { TagsApp } from 'apps/tag/app';
import { clientStore } from 'apps/client/app';
import { ProfilesApp } from 'apps/profile/app';
import { raiseUserError } from 'apps/utils';

import { InitiativeModel } from 'apps/initiative/app';
import { NewInitiativeMutation } from 'apps/initiative/gql/index';
import { conf } from 'apps/initiative/config.yaml';

class NewInitiativeApp extends App {
  static registryName = 'NewInitiativeApp';
  static AppProps = {
    tags: TagsApp,
  };

  static Model = InitiativeModel;
  init() {
    this.wizard = new WizardStore(this.manager, conf, 'initiativeWizard', {
      createInitiative: this.CreateInitiative,
    });
  }

  @action onSysErr = () => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    Alert.error(
      i18next.t(
        'Hmm... We failed to create your Organisation. Please try again.',
      ),
    );
  };

  @action onUserErr = errs => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    this.wizard.ToErrorForm(errs);
    raiseUserError(errs, 'create', 'Initiative');
  };

  @action onSuccess = result => {
    clientStore.SetPosting(false);
    this.wizard.clearDraft();
    const { routerStore } = clientStore;
    if (this.manager.newInitiativeInput.startFreeTrial) {
      analytics.sendEvent('start-free-trial', {
        space: `+${result.slug}`,
      });
      analytics.sendEvent(
        'StartTrial',
        { value: 7, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
    }
    analytics.sendEvent('created-organization', {}, { ga: true });
    routerStore.navigate('home');

    clientStore.QueryAvailableSpaces(false, () => {
      clientStore.SetActiveWorkspaceWithSlugIfValid(result.slug);
    });
  };

  @action CreateInitiative = () => {
    if (this.wizard.activeForm.isValid) {
      this.wizard.activeForm.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.CreateInitiative({
        onSysErr: this.onSysErr,
        onUserErr: this.onUserErr,
        onSuccess: this.onSuccess,
      });
    } else {
      this.wizard.activeForm.alert();
    }
  };
}

export class NewInitiativeAppManager extends AppManager {
  static registryName = 'NewInitiativeAppManager';
  @observable website = '';
  @observable twitter = '';
  @observable facebook = '';
  @observable emailInvites = [];
  constructor(parent, init = {}) {
    super(parent, init);

    this.AppRef('invites', ProfilesApp);
    this.invites = new ProfilesApp(this);

    this.newInitiativeApp = new NewInitiativeApp(this, { tags: new TagsApp() });
    this.newInitiativeApp.init();
    this.newInitiativeInput = new Input({
      title: () => this.newInitiativeApp.title,
      avatar: () => '',
      slug: () => this.newInitiativeApp.slug,
      description: () => this.newInitiativeApp.description,
      organizationTypeId: () => this.newInitiativeApp.organizationType,
      tags: () =>
        this.newInitiativeApp.tags.map(({ id, name, deleted }) => ({
          id,
          name,
          deleted,
        })),
      socialAccounts: () => {
        const accounts = [];
        if (this.website) {
          accounts.push({ accountType: 'WEBSITE', accountName: this.website });
        }
        if (this.twitter) {
          accounts.push({ accountType: 'TWITTER', accountName: this.twitter });
        }
        if (this.facebook) {
          accounts.push({
            accountType: 'FACEBOOK',
            accountName: this.facebook,
          });
        }
        return accounts;
      },
      invites: () => this.invites.map(({ username }) => username),
      startFreeTrial: () => this.params.get('trial'),
    });
  }

  @action CreateInitiative = options => {
    this.mutations.push(
      NewInitiativeMutation.New(this.newInitiativeInput, options),
    );
  };

  @action UpdateValue(field, value) {
    this[field] = value;
    if (field === 'initiativeName') {
      this.wikifactoryUrl = `wikifactory.com/+${value
        .replace(/\s/g, '')
        .toLowerCase()}`;
    }
  }
}
